import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { getClientInvoices, deleteInvoice, updateInvoice, markInvoiceAsPaid, toggleDepositCollected } from '../../services/invoices';
import { sendEstimateEmail, sendInvoiceEmail } from '../../services/email';
import { createAuditLog } from '../../services/audit';
import { getEnableDueDates } from '../../services/settings';
import { getLetterTemplate, getDefaultLetterTemplates } from '../../services/letterTemplates';
import { renderTemplate } from '../../utils/templateRenderer';
import { assembleDocument } from '../../utils/assembleDocument';
import './InvoiceHistoryTab.css';

export default function InvoiceHistoryTab({ clientId, client, scrollToInvoiceId }) {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printInvoice, setPrintInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editFormData, setEditFormData] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const autoSaveTimeoutRef = useRef(null);
  const hasEditChangesRef = useRef(false);
  const invoiceRefs = useRef({});
  const [letterTemplate, setLetterTemplate] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [enableDueDates, setEnableDueDates] = useState(true);

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getClientInvoices(clientId);
      setInvoices(data);
    } catch (error) {
      console.error('Error loading invoices:', error);
      alert('Failed to load invoices: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // Load enable due dates setting
  useEffect(() => {
    getEnableDueDates().then(setEnableDueDates).catch(() => setEnableDueDates(true));
  }, []);

  // Scroll to specific invoice when it loads
  useEffect(() => {
    if (scrollToInvoiceId && invoices.length > 0 && !loading) {
      // Wait a bit for the DOM to render
      setTimeout(() => {
        const invoiceElement = invoiceRefs.current[scrollToInvoiceId];
        if (invoiceElement) {
          invoiceElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Highlight the invoice card briefly
          invoiceElement.style.transition = 'box-shadow 0.3s';
          invoiceElement.style.boxShadow = '0 0 0 3px rgba(49, 130, 206, 0.5)';
          setTimeout(() => {
            invoiceElement.style.boxShadow = '';
          }, 2000);
        }
      }, 300);
    }
  }, [scrollToInvoiceId, invoices, loading]);

  const handleDelete = async (invoiceId) => {
    // Find the invoice to check its status
    const invoice = invoices.find(inv => inv.id === invoiceId);
    
    // Prevent deletion of paid invoices
    if (invoice && invoice.status === 'paid' && invoice.type === 'invoice') {
      alert('Cannot delete paid invoices. Paid invoices are permanent records.');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await deleteInvoice(invoiceId);
        await createAuditLog('delete', invoice?.type === 'estimate' ? 'estimate' : 'invoice', invoiceId, {
          invoice_number: invoice?.invoice_number,
          client_name: client?.name
        });
        loadInvoices();
      } catch (error) {
        alert('Failed to delete document: ' + error.message);
      }
    }
  };

  const handleFinalize = async (invoiceId) => {
    if (window.confirm('Are you sure you want to finalize this quote? It will be converted to an invoice.')) {
      try {
        const invoice = invoices.find(inv => inv.id === invoiceId);
        await updateInvoice(invoiceId, { type: 'invoice', status: 'sent' });
        await createAuditLog('finalize', 'estimate', invoiceId, {
          invoice_number: invoice?.invoice_number,
          client_name: client?.name
        });
        loadInvoices();
        // Trigger event to refresh open quotes banner
        window.dispatchEvent(new Event('quoteFinalized'));
        alert('Quote finalized successfully!');
      } catch (error) {
        alert('Failed to finalize estimate: ' + error.message);
      }
    }
  };

  const handleFinalizeDraft = async (invoiceId) => {
    const invoice = invoices.find(inv => inv.id === invoiceId);
    const docType = invoice?.type === 'estimate' ? 'estimate' : 'invoice';
    const newStatus = invoice?.type === 'estimate' ? 'sent' : 'unpaid';
    
    if (window.confirm(`Are you sure you want to finalize this draft ${docType}? It will no longer be editable as a draft.`)) {
      try {
        await updateInvoice(invoiceId, { status: newStatus });
        await createAuditLog('finalize_draft', docType, invoiceId, {
          invoice_number: invoice?.invoice_number,
          client_name: client?.name
        });
        loadInvoices();
        alert('Draft finalized successfully!');
      } catch (error) {
        alert('Failed to finalize draft: ' + error.message);
      }
    }
  };

  const handleEdit = (invoice) => {
    const items = parseItems(invoice.items);
    setEditFormData({
      id: invoice.id,
      hide_totals: invoice.hide_totals || false,
      include_tax: invoice.include_tax !== false,
      invoice_number: invoice.invoice_number || '',
      type: invoice.type || 'estimate',
      date: invoice.date || new Date().toISOString().split('T')[0],
      due_date: invoice.due_date || '',
      items: items.length > 0 ? items : [{ description: '', quantity: 1, unit_price: 0, taxable: true, total: 0 }],
      subtotal: invoice.subtotal || 0,
      taxable_subtotal: invoice.taxable_subtotal || 0,
      non_taxable_subtotal: invoice.non_taxable_subtotal || 0,
      tax_rate: invoice.tax_rate || 0,
      tax_amount: invoice.tax_amount || 0,
      total: invoice.total || 0,
      notes: invoice.notes || '',
      status: invoice.status || 'draft',
    });
    setEditingInvoice(invoice);
  };

  const handleEditCancel = () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    hasEditChangesRef.current = false;
    setAutoSaveStatus('');
    setEditingInvoice(null);
    setEditFormData(null);
  };

  const calculateItemTotal = (quantity, unitPrice) => {
    return (parseFloat(quantity) || 0) * (parseFloat(unitPrice) || 0);
  };

  const updateEditItem = (index, field, value) => {
    hasEditChangesRef.current = true;
    setEditFormData(prev => {
      const newItems = [...prev.items];
      const updatedItem = { ...newItems[index], [field]: value };
      
      if (field === 'quantity' || field === 'unit_price') {
        updatedItem.total = calculateItemTotal(updatedItem.quantity, updatedItem.unit_price);
      }

      newItems[index] = updatedItem;
      
      // Calculate subtotals - exclude separators
      const taxableSubtotal = newItems
        .filter(item => !item.isSeparator && item.taxable)
        .reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
      
      const nonTaxableSubtotal = newItems
        .filter(item => !item.isSeparator && !item.taxable)
        .reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
      
      const totalSubtotal = taxableSubtotal + nonTaxableSubtotal;
      const taxAmount = prev.include_tax !== false ? taxableSubtotal * (parseFloat(prev.tax_rate) || 0) / 100 : 0;
      const total = totalSubtotal + taxAmount;
      
      return {
        ...prev,
        items: newItems,
        subtotal: totalSubtotal.toFixed(2),
        taxable_subtotal: taxableSubtotal.toFixed(2),
        non_taxable_subtotal: nonTaxableSubtotal.toFixed(2),
        tax_amount: taxAmount.toFixed(2),
        total: total.toFixed(2),
      };
    });
  };

  const addEditItem = () => {
    hasEditChangesRef.current = true;
    setEditFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, unit_price: 0, taxable: true, total: 0 }],
    }));
  };

  const addEditSeparator = (index) => {
    hasEditChangesRef.current = true;
    setEditFormData(prev => {
      const newItems = [...prev.items];
      newItems.splice(index + 1, 0, { isSeparator: true, showSubtotal: false, description: '', quantity: 0, unit_price: 0, taxable: false, total: 0 });
      return { ...prev, items: newItems };
    });
  };

  const toggleEditSeparatorSubtotal = (index) => {
    hasEditChangesRef.current = true;
    setEditFormData(prev => {
      const newItems = [...prev.items];
      if (newItems[index]?.isSeparator) {
        newItems[index] = { ...newItems[index], showSubtotal: !newItems[index].showSubtotal };
      }
      return { ...prev, items: newItems };
    });
  };

  const removeEditItem = (index) => {
    hasEditChangesRef.current = true;
    setEditFormData(prev => {
      const newItems = prev.items.filter((_, i) => i !== index);
      
      const taxableSubtotal = newItems
        .filter(item => !item.isSeparator && item.taxable)
        .reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
      
      const nonTaxableSubtotal = newItems
        .filter(item => !item.isSeparator && !item.taxable)
        .reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
      
      const totalSubtotal = taxableSubtotal + nonTaxableSubtotal;
      const taxAmount = prev.include_tax !== false ? taxableSubtotal * (parseFloat(prev.tax_rate) || 0) / 100 : 0;
      const total = totalSubtotal + taxAmount;
      
      return {
        ...prev,
        items: newItems,
        subtotal: totalSubtotal.toFixed(2),
        taxable_subtotal: taxableSubtotal.toFixed(2),
        non_taxable_subtotal: nonTaxableSubtotal.toFixed(2),
        tax_amount: taxAmount.toFixed(2),
        total: total.toFixed(2),
      };
    });
  };

  const handleEditTaxRateChange = (e) => {
    hasEditChangesRef.current = true;
    const taxRate = parseFloat(e.target.value) || 0;
    setEditFormData(prev => {
      const taxableSubtotal = parseFloat(prev.taxable_subtotal) || 0;
      const nonTaxableSubtotal = parseFloat(prev.non_taxable_subtotal) || 0;
      const totalSubtotal = taxableSubtotal + nonTaxableSubtotal;
      const taxAmount = prev.include_tax !== false ? taxableSubtotal * taxRate / 100 : 0;
      const total = totalSubtotal + taxAmount;
      
      return {
        ...prev,
        tax_rate: taxRate,
        tax_amount: taxAmount.toFixed(2),
        total: total.toFixed(2),
      };
    });
  };

  // Auto-save function for edit modal
  const autoSaveEdit = useCallback(async () => {
    if (!hasEditChangesRef.current || !editFormData?.id || !clientId) return;

    setAutoSaveStatus('Auto-saving...');
    try {
      const invoiceToSave = {
        ...editFormData,
        items: JSON.stringify(editFormData.items),
        subtotal: parseFloat(editFormData.subtotal),
        taxable_subtotal: parseFloat(editFormData.taxable_subtotal || 0),
        non_taxable_subtotal: parseFloat(editFormData.non_taxable_subtotal || 0),
        tax_rate: parseFloat(editFormData.tax_rate),
        tax_amount: parseFloat(editFormData.tax_amount),
        total: parseFloat(editFormData.total),
        status: 'draft', // Ensure it stays as draft during editing
        due_date: enableDueDates && editFormData.due_date ? editFormData.due_date : null,
      };

      await updateInvoice(editFormData.id, invoiceToSave);
      hasEditChangesRef.current = false;
      setAutoSaveStatus('Auto-saved');
      setTimeout(() => setAutoSaveStatus(''), 2000);
    } catch (error) {
      console.error('Error auto-saving:', error);
      setAutoSaveStatus('Auto-save failed');
      setTimeout(() => setAutoSaveStatus(''), 3000);
    }
  }, [editFormData, clientId, enableDueDates]);

  // Debounced auto-save for edit modal
  useEffect(() => {
    if (!editingInvoice || !editFormData) return;

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    if (hasEditChangesRef.current) {
      autoSaveTimeoutRef.current = setTimeout(() => {
        autoSaveEdit();
      }, 3000); // Auto-save 3 seconds after last change
    }

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [editFormData, autoSaveEdit, editingInvoice]);

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const invoiceToSave = {
        ...editFormData,
        items: JSON.stringify(editFormData.items),
        subtotal: parseFloat(editFormData.subtotal),
        taxable_subtotal: parseFloat(editFormData.taxable_subtotal || 0),
        non_taxable_subtotal: parseFloat(editFormData.non_taxable_subtotal || 0),
        tax_rate: parseFloat(editFormData.tax_rate),
        tax_amount: parseFloat(editFormData.tax_amount),
        total: parseFloat(editFormData.total),
        hide_totals: editFormData.hide_totals || false,
        include_tax: editFormData.include_tax !== false,
        due_date: enableDueDates && editFormData.due_date ? editFormData.due_date : null,
      };

      await updateInvoice(editFormData.id, invoiceToSave);
      await createAuditLog('update', editFormData.type === 'quote' ? 'quote' : 'invoice', editFormData.id, {
        invoice_number: editFormData.invoice_number,
        client_name: client?.name,
        total: editFormData.total
      });
      
      loadInvoices();
      setEditingInvoice(null);
      setEditFormData(null);
      alert('Invoice/Estimate updated successfully!');
    } catch (error) {
      alert('Failed to update invoice/estimate: ' + error.message);
    }
  };

  const handleMarkAsPaid = async (invoiceId, invoiceNumber) => {
    if (window.confirm(`Mark invoice ${invoiceNumber || invoiceId} as paid?`)) {
      try {
        const invoice = invoices.find(inv => inv.id === invoiceId);
        await markInvoiceAsPaid(invoiceId);
        await createAuditLog('mark_paid', 'invoice', invoiceId, {
          invoice_number: invoiceNumber,
          client_name: client?.name,
          total: invoice?.total
        });
        loadInvoices();
        // Trigger event to refresh unpaid invoices banner
        window.dispatchEvent(new Event('invoicePaid'));
        alert('Invoice marked as paid successfully!');
      } catch (error) {
        alert('Failed to mark invoice as paid: ' + error.message);
      }
    }
  };

  const handleDepositToggle = async (invoiceId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await toggleDepositCollected(invoiceId, newStatus);
      await createAuditLog('update', 'invoice', invoiceId, {
        action: 'deposit_collected_toggled',
        deposit_collected: newStatus,
        client_name: client?.name
      });
      loadInvoices();
      // Trigger event to refresh unpaid invoices banner
      window.dispatchEvent(new Event('invoiceUpdated'));
    } catch (error) {
      alert('Failed to update deposit status: ' + error.message);
    }
  };

  const handleHideTotalsToggle = async (invoiceId, currentValue) => {
    try {
      const newValue = !currentValue;
      await updateInvoice(invoiceId, { hide_totals: newValue });
      await loadInvoices();
    } catch (error) {
      alert('Failed to update hide totals status: ' + error.message);
    }
  };

  const handlePrint = (invoice) => {
    setPrintInvoice(invoice);
  };

  const handleEmail = async (invoice) => {
    try {
      if (!client || !client.email) {
        alert('Client email is required to send email');
        return;
      }

      // Use estimate template only when document is an estimate; otherwise use invoice (unpaid or receipt) template
      const isEstimate = invoice.type === 'estimate';
      const docType = isEstimate ? 'estimate' : (invoice.status === 'paid' ? 'receipt' : 'invoice');
      alert(`A PDF ${docType} will be downloaded. After it downloads, your email client will open. Please attach the downloaded PDF file before sending the email.`);

      if (isEstimate) {
        await sendEstimateEmail(invoice, client);
      } else {
        await sendInvoiceEmail(invoice, client);
      }
    } catch (error) {
      alert('Failed to send email: ' + error.message);
    }
  };

  const parseItems = (items) => {
    try {
      return typeof items === 'string' ? JSON.parse(items) : items;
    } catch {
      return [];
    }
  };

  // Ensure every line item has unit_price and total as numbers (snake_case) for print/PDF
  const normalizeLineItemsForPrint = (rawItems) => {
    const arr = Array.isArray(rawItems) ? rawItems : [];
    return arr.map((item) => {
      if (!item || typeof item !== 'object') return item;
      const unitPrice = item.unit_price ?? item.unitPrice ?? 0;
      const numUnitPrice = parseFloat(unitPrice) || 0;
      const qty = parseFloat(item.quantity ?? item.qty ?? 0) || 0;
      const total = item.total !== undefined && item.total !== null && item.total !== ''
        ? (parseFloat(item.total) || 0)
        : numUnitPrice * qty;
      return {
        ...item,
        description: item.description ?? item.Description ?? '',
        quantity: qty,
        unit_price: numUnitPrice,
        taxable: item.taxable !== false,
        total: total,
      };
    });
  };

  // Load letter template for print view
  useEffect(() => {
    const loadTemplate = async () => {
      if (!printInvoice) return;
      
      try {
        setLoadingTemplate(true);
        const templateType = (printInvoice.type === 'estimate' || !printInvoice.type) ? 'estimate' : 'invoice';
        const template = await getLetterTemplate(templateType);
        setLetterTemplate(template);
      } catch (error) {
        console.log('Using default letter template:', error);
        const defaults = getDefaultLetterTemplates();
        const templateType = (printInvoice.type === 'estimate' || !printInvoice.type) ? 'estimate' : 'invoice';
        setLetterTemplate(defaults[templateType]);
      } finally {
        setLoadingTemplate(false);
      }
    };
    
    loadTemplate();
  }, [printInvoice]);

  // Open print dialog only after template has loaded and content has rendered
  useEffect(() => {
    if (!printInvoice || !letterTemplate || loadingTemplate) return;
    const timeoutId = setTimeout(() => window.print(), 150);
    const onAfterPrint = () => {
      setPrintInvoice(null);
      window.removeEventListener('afterprint', onAfterPrint);
    };
    window.addEventListener('afterprint', onAfterPrint);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('afterprint', onAfterPrint);
    };
  }, [printInvoice, letterTemplate, loadingTemplate]);

  // Print view component - uses template from Settings
  const PrintView = ({ invoice }) => {
    if (loadingTemplate || !letterTemplate) {
      return <div className="print-view">Loading template...</div>;
    }
    
    // Prepare template data (normalize items so unit_price/total are always numbers for print)
    const rawItems = parseItems(invoice.items);
    const items = normalizeLineItemsForPrint(rawItems);
    const clientCityStateZip = [client.city, client.state, client.zip].filter(Boolean).join(', ') || '';
    
    const templateData = {
      document_type: (invoice.type === 'estimate' || !invoice.type) ? 'ESTIMATE' : 'INVOICE',
      client: {
        name: client.name || 'Client',
        address: client.address || '',
        city_state_zip: clientCityStateZip,
        email: client.email || '',
        phone: client.phone || '',
      },
      invoice: {
        number: invoice.invoice_number || invoice.id || '',
        date: invoice.date ? new Date(invoice.date).toLocaleDateString() : '',
        due_date: invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '',
        document_type: (invoice.type === 'estimate' || !invoice.type) ? 'ESTIMATE' : 'INVOICE',
        subtotal: `$${parseFloat(invoice.subtotal || 0).toFixed(2)}`,
        tax: `$${parseFloat(invoice.tax_amount || 0).toFixed(2)}`,
        tax_amount: `$${parseFloat(invoice.tax_amount || 0).toFixed(2)}`,
        total: `$${parseFloat(invoice.total || 0).toFixed(2)}`,
        tax_rate: invoice.tax_rate || 0,
        taxable_subtotal: invoice.taxable_subtotal || 0,
        non_taxable_subtotal: invoice.non_taxable_subtotal || 0,
        line_items: items,
        items_table: items,
        notes: invoice.notes || '',
      },
      date: invoice.date ? new Date(invoice.date).toLocaleDateString() : '',
      due_date: invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '',
      facility: {
        name: 'Menlo Atherton Glass',
        address: 'PO Box 5214, Redwood City CA 94063',
      },
      user: {
        name: user?.user_metadata?.full_name || user?.email || '',
      },
      notes_section: invoice.notes || '',
      tax_rate: invoice.tax_rate || 0,
    };
    
    // UNIFIED PIPELINE: Assemble document then render
    // Step 1: Assemble document structure
    const assembledHTML = assembleDocument({
      header: letterTemplate.header || '',
      body: letterTemplate.body || '',
      footer: letterTemplate.footer || ''
    });
    
    // Step 2: Render with real data using unified renderTemplate
    const templateContent = renderTemplate(assembledHTML, templateData, invoice.hide_totals);
    
    return (
      <div className="print-view">
        <div 
          className="print-template-content"
          dangerouslySetInnerHTML={{ __html: templateContent }}
        />
      </div>
    );
  };

  if (printInvoice) {
    return createPortal(<PrintView invoice={printInvoice} />, document.body);
  }

  if (loading) {
    return (
      <div className="invoice-history-tab">
        <div className="loading-state">Loading documents...</div>
      </div>
    );
  }

  return (
    <div className="invoice-history-tab">
      <div className="tab-header">
        <h2>Invoice & Estimate History</h2>
      </div>

      {invoices.length === 0 ? (
        <div className="empty-state">
          <p>No invoices or estimates found for this client.</p>
        </div>
      ) : (
        <div className="invoices-list">
          {invoices.map((invoice) => {
            const items = parseItems(invoice.items);
            const isEstimate = invoice.type === 'estimate' || (!invoice.type && invoice.status === 'draft');
            
            return (
              <div 
                key={invoice.id} 
                className="invoice-card"
                ref={(el) => {
                  if (el) {
                    invoiceRefs.current[invoice.id] = el;
                  }
                }}
              >
                <div className="invoice-header">
                  <div className="invoice-info">
                    <h3>
                      {invoice.invoice_number || `${isEstimate ? 'Estimate' : 'Invoice'} #${invoice.id}`}
                      {isEstimate && <span className="estimate-badge">Estimate</span>}
                    </h3>
                    <div className="invoice-meta">
                      <span>Date: {new Date(invoice.date).toLocaleDateString()}</span>
                      {invoice.due_date && (
                        <span>Due: {new Date(invoice.due_date).toLocaleDateString()}</span>
                      )}
                      {isEstimate && invoice.created_by && (
                        <span className="creator-badge">
                          Created by: {invoice.created_by_user?.email || 'User ' + invoice.created_by.substring(0, 8)}
                        </span>
                      )}
                      <span className={`status-badge ${invoice.status}`}>
                        {invoice.status}
                      </span>
                    </div>
                  </div>
                  <div className="invoice-actions">
                    <span className="invoice-total">${parseFloat(invoice.total || 0).toFixed(2)}</span>
                    <div className="action-buttons">
                      <button
                        className="btn-action"
                        onClick={() => handlePrint(invoice)}
                        title="Print"
                      >
                        Print
                      </button>
                      <button
                        className="btn-action"
                        onClick={() => handleEmail(invoice)}
                        title="Email"
                        disabled={!client?.email}
                      >
                        Email
                      </button>
                      {invoice.status !== 'paid' && (
                        <button
                          className="btn-edit"
                          onClick={() => handleEdit(invoice)}
                          title="Edit"
                        >
                          Edit
                        </button>
                      )}
                      {invoice.status === 'draft' && (
                        <button
                          className="btn-finalize-draft"
                          onClick={() => handleFinalizeDraft(invoice.id)}
                          title="Finalize Draft"
                        >
                          Finalize Draft
                        </button>
                      )}
                      {isEstimate && invoice.status !== 'draft' && (
                        <button
                          className="btn-finalize"
                          onClick={() => handleFinalize(invoice.id)}
                          title="Finalize Estimate"
                        >
                          Finalize
                        </button>
                      )}
                      {!isEstimate && invoice.status !== 'paid' && (
                        <>
                          <label className="deposit-checkbox-label">
                            <input
                              type="checkbox"
                              checked={invoice.deposit_collected || false}
                              onChange={() => handleDepositToggle(invoice.id, invoice.deposit_collected)}
                            />
                            <span>Deposit Collected</span>
                          </label>
                          <button
                            className="btn-mark-paid"
                            onClick={() => handleMarkAsPaid(invoice.id, invoice.invoice_number)}
                            title="Mark as Paid"
                          >
                            Mark as Paid
                          </button>
                        </>
                      )}
                      <label className="hide-totals-checkbox-label">
                        <input
                          type="checkbox"
                          checked={invoice.hide_totals || false}
                          onChange={() => handleHideTotalsToggle(invoice.id, invoice.hide_totals)}
                        />
                        <span>Hide Totals</span>
                      </label>
                      {invoice.status !== 'paid' && (
                        <button
                          className="btn-delete-invoice"
                          onClick={() => handleDelete(invoice.id)}
                          title="Delete"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {items.length > 0 && (
                  <div className="invoice-items">
                    <table>
                      <thead>
                        <tr>
                          <th>Description</th>
                          <th>Qty</th>
                          <th>Unit Price</th>
                          <th>Taxable</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => {
                          if (item.isSeparator) {
                            const prevSepIndex = items.slice(0, index).findLastIndex((x) => x.isSeparator);
                            const sectionStart = prevSepIndex >= 0 ? prevSepIndex + 1 : 0;
                            const sectionItems = items.slice(sectionStart, index).filter((i) => !i.isSeparator);
                            const sectionSum = sectionItems.reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
                            const sectionTaxableSum = sectionItems
                              .filter((i) => i.taxable !== false)
                              .reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
                            const includeTax = invoice.include_tax !== false;
                            const taxRate = parseFloat(invoice.tax_rate || 0);
                            const hasSectionTax = includeTax && taxRate > 0 && sectionTaxableSum > 0;
                            const sectionTax = hasSectionTax ? sectionTaxableSum * taxRate / 100 : 0;
                            const sectionTotalWithTax = sectionSum + sectionTax;
                            return (
                              <React.Fragment key={index}>
                                {item.showSubtotal && sectionSum > 0 && (
                                  <>
                                    {hasSectionTax && (
                                      <>
                                        <tr className="item-section-subtotal-row">
                                          <td colSpan={4} className="section-subtotal-label">Section Subtotal</td>
                                          <td className="total-cell section-subtotal-value">${sectionSum.toFixed(2)}</td>
                                        </tr>
                                        <tr className="item-section-tax-row">
                                          <td colSpan={4} className="section-subtotal-label">Section Tax ({taxRate}%)</td>
                                          <td className="total-cell section-subtotal-value">${sectionTax.toFixed(2)}</td>
                                        </tr>
                                      </>
                                    )}
                                    <tr className="item-section-subtotal-row">
                                      <td colSpan={4} className="section-subtotal-label">Section Total</td>
                                      <td className="total-cell section-subtotal-value">${sectionTotalWithTax.toFixed(2)}</td>
                                    </tr>
                                  </>
                                )}
                                <tr className="item-separator-row">
                                  <td colSpan="5" className="item-separator">
                                    <div className="separator-line"></div>
                                  </td>
                                </tr>
                              </React.Fragment>
                            );
                          }
                          return (
                            <tr key={index}>
                              <td>{item.description}</td>
                              <td>{item.quantity}</td>
                              <td>${parseFloat(item.unit_price || 0).toFixed(2)}</td>
                              <td>{item.taxable !== false ? 'Yes' : 'No'}</td>
                              <td>${parseFloat(item.total || 0).toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {!invoice.hide_totals && (
                  <div className="invoice-summary">
                    <div className="summary-row">
                      <span>Subtotal:</span>
                      <span>${parseFloat(invoice.subtotal || 0).toFixed(2)}</span>
                    </div>
                    {parseFloat(invoice.taxable_subtotal || 0) > 0 && (
                      <div className="summary-row">
                        <span>Taxable Amount:</span>
                        <span>${parseFloat(invoice.taxable_subtotal || 0).toFixed(2)}</span>
                      </div>
                    )}
                    {parseFloat(invoice.non_taxable_subtotal || 0) > 0 && (
                      <div className="summary-row">
                        <span>Non-Taxable Amount:</span>
                        <span>${parseFloat(invoice.non_taxable_subtotal || 0).toFixed(2)}</span>
                      </div>
                    )}
                    {parseFloat(invoice.tax_rate || 0) > 0 && (
                      <div className="summary-row">
                        <span>Tax ({invoice.tax_rate}%):</span>
                        <span>${parseFloat(invoice.tax_amount || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="summary-row total">
                      <span>Total:</span>
                      <span>${parseFloat(invoice.total || 0).toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {invoice.notes && (
                  <div className="invoice-notes">
                    <strong>Notes:</strong> {invoice.notes}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Modal */}
      {editingInvoice && editFormData && (
        <div className="edit-modal-overlay" onClick={handleEditCancel}>
          <div className="edit-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="edit-modal-header">
              <div>
                <h2>Edit {editFormData.type === 'estimate' ? 'Estimate' : 'Invoice'}</h2>
                {autoSaveStatus && (
                  <div className={`auto-save-status ${autoSaveStatus.includes('saved') ? 'saved' : ''}`}>
                    {autoSaveStatus}
                  </div>
                )}
              </div>
              <button className="btn-close-modal" onClick={handleEditCancel}>×</button>
            </div>
            
            <form onSubmit={handleEditSubmit} className="edit-invoice-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Document #</label>
                  <input
                    type="text"
                    value={editFormData.invoice_number}
                    readOnly
                    className="invoice-number-input"
                  />
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={editFormData.date}
                    onChange={(e) => {
                      hasEditChangesRef.current = true;
                      setEditFormData(prev => ({ ...prev, date: e.target.value }));
                    }}
                    required
                  />
                </div>
                {enableDueDates && (
                  <div className="form-group">
                    <label>Due Date</label>
                    <input
                      type="date"
                      value={editFormData.due_date}
                      onChange={(e) => {
                        hasEditChangesRef.current = true;
                        setEditFormData(prev => ({ ...prev, due_date: e.target.value }));
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="invoice-items-section">
                <div className="section-header">
                  <h3>Items</h3>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="button" className="btn-add-item" onClick={addEditItem}>
                      + Add Item
                    </button>
                    <button type="button" className="btn-add-separator" onClick={() => addEditSeparator(editFormData.items.length - 1)}>
                      + Add Separator
                    </button>
                  </div>
                </div>
                <div className="items-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Qty</th>
                        <th>Unit Price</th>
                        <th>Taxable</th>
                        <th>Total</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {editFormData.items.map((item, index) => {
                        if (item.isSeparator) {
                          const prevSepIndex = editFormData.items.slice(0, index).findLastIndex((x) => x.isSeparator);
                          const sectionStart = prevSepIndex >= 0 ? prevSepIndex + 1 : 0;
                          const sectionItems = editFormData.items.slice(sectionStart, index).filter((i) => !i.isSeparator);
                          const sectionSum = sectionItems.reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
                          const sectionTaxableSum = sectionItems
                            .filter((i) => i.taxable !== false)
                            .reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
                          const includeTax = editFormData.include_tax !== false;
                          const taxRate = parseFloat(editFormData.tax_rate || 0);
                          const hasSectionTax = includeTax && taxRate > 0 && sectionTaxableSum > 0;
                          const sectionTax = hasSectionTax ? sectionTaxableSum * taxRate / 100 : 0;
                          const sectionTotalWithTax = sectionSum + sectionTax;
                          return (
                            <React.Fragment key={index}>
                              {item.showSubtotal && sectionSum > 0 && (
                                <>
                                  {hasSectionTax && (
                                    <>
                                      <tr className="item-section-subtotal-row">
                                        <td colSpan={4} className="section-subtotal-label">Section Subtotal</td>
                                        <td className="total-cell section-subtotal-value">${sectionSum.toFixed(2)}</td>
                                        <td></td>
                                      </tr>
                                      <tr className="item-section-tax-row">
                                        <td colSpan={4} className="section-subtotal-label">Section Tax ({taxRate}%)</td>
                                        <td className="total-cell section-subtotal-value">${sectionTax.toFixed(2)}</td>
                                        <td></td>
                                      </tr>
                                    </>
                                  )}
                                  <tr className="item-section-subtotal-row">
                                    <td colSpan={4} className="section-subtotal-label">Section Total</td>
                                    <td className="total-cell section-subtotal-value">${sectionTotalWithTax.toFixed(2)}</td>
                                    <td></td>
                                  </tr>
                                </>
                              )}
                              <tr className="item-separator-row">
                                <td colSpan={6} className="item-separator">
                                  <div className="separator-line"></div>
                                  <button
                                    type="button"
                                    className={`btn-subtotal-toggle ${item.showSubtotal ? 'active' : ''}`}
                                    onClick={() => toggleEditSeparatorSubtotal(index)}
                                    title={item.showSubtotal ? 'Hide section subtotal' : 'Show section subtotal'}
                                  >
                                    Subtotal
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-remove-item"
                                    onClick={() => removeEditItem(index)}
                                    style={{ marginLeft: '1rem' }}
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            </React.Fragment>
                          );
                        }
                        return (
                          <tr key={index}>
                            <td>
                              <input
                                type="text"
                                value={item.description || ''}
                                onChange={(e) => updateEditItem(index, 'description', e.target.value)}
                                placeholder="Item description"
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                value={item.quantity || ''}
                                onChange={(e) => updateEditItem(index, 'quantity', e.target.value)}
                                required
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                value={item.unit_price || ''}
                                onChange={(e) => updateEditItem(index, 'unit_price', e.target.value)}
                                required
                              />
                            </td>
                            <td className="taxable-cell">
                              <input
                                type="checkbox"
                                checked={item.taxable}
                                onChange={(e) => updateEditItem(index, 'taxable', e.target.checked)}
                              />
                            </td>
                            <td className="total-cell">
                              ${parseFloat(item.total || 0).toFixed(2)}
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <button
                                  type="button"
                                  className="btn-add-separator"
                                  onClick={() => addEditSeparator(index)}
                                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                >
                                  Add Line
                                </button>
                                <button
                                  type="button"
                                  className="btn-remove-item"
                                  onClick={() => removeEditItem(index)}
                                >
                                  Remove
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group full-width">
                  <label>Notes</label>
                  <textarea
                    rows="4"
                    value={editFormData.notes}
                    onChange={(e) => {
                      hasEditChangesRef.current = true;
                      setEditFormData(prev => ({ ...prev, notes: e.target.value }));
                    }}
                    placeholder="Additional notes or terms..."
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editFormData.include_tax !== false}
                      onChange={(e) => {
                        hasEditChangesRef.current = true;
                        const includeTax = e.target.checked;
                        setEditFormData(prev => {
                          const taxAmount = includeTax ? parseFloat(prev.taxable_subtotal || 0) * (parseFloat(prev.tax_rate) || 0) / 100 : 0;
                          const total = parseFloat(prev.subtotal || 0) + taxAmount;
                          return {
                            ...prev,
                            include_tax: includeTax,
                            tax_amount: taxAmount.toFixed(2),
                            total: total.toFixed(2),
                          };
                        });
                      }}
                    />
                    <span>Include tax in total</span>
                  </label>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editFormData.hide_totals || false}
                      onChange={(e) => {
                        hasEditChangesRef.current = true;
                        setEditFormData(prev => ({ ...prev, hide_totals: e.target.checked }));
                      }}
                    />
                    <span>Hide totals (for option selection - client can choose which option they prefer)</span>
                  </label>
                </div>
              </div>

              <div className="invoice-totals">
                <div className="totals-row">
                  <span className="totals-label">Subtotal:</span>
                  <span className="totals-value">${parseFloat(editFormData.subtotal || 0).toFixed(2)}</span>
                </div>
                {parseFloat(editFormData.taxable_subtotal || 0) > 0 && (
                  <div className="totals-row">
                    <span className="totals-label">Taxable Amount:</span>
                    <span className="totals-value">${parseFloat(editFormData.taxable_subtotal || 0).toFixed(2)}</span>
                  </div>
                )}
                {parseFloat(editFormData.non_taxable_subtotal || 0) > 0 && (
                  <div className="totals-row">
                    <span className="totals-label">Non-Taxable Amount:</span>
                    <span className="totals-value">${parseFloat(editFormData.non_taxable_subtotal || 0).toFixed(2)}</span>
                  </div>
                )}
                {editFormData.include_tax !== false && (
                  <div className="totals-row">
                    <div className="tax-input-group">
                      <label>Tax Rate (%):</label>
                      <input
                        type="number"
                        value={editFormData.tax_rate}
                        onChange={handleEditTaxRateChange}
                        className="tax-input"
                      />
                    </div>
                    <span className="totals-value">${parseFloat(editFormData.tax_amount || 0).toFixed(2)}</span>
                  </div>
                )}
                <div className="totals-row total-row">
                  <span className="totals-label">Total:</span>
                  <span className="totals-value">${parseFloat(editFormData.total || 0).toFixed(2)}</span>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={handleEditCancel}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
